import type { TourStep, TourVariant } from '@gokrew/ui';
import { ClockState } from '@gokrew/models/timesheet';

// ─── Dashboard ───────────────────────────────────────────────────────────────

/**
 * Factory so the announcements step is only included when the announcements card
 * is actually rendered (`ENABLE_ANNOUNCEMENT`). Targeting a card that isn't in the
 * DOM would leave Joyride stuck on a missing target. See the company portal's
 * `buildDashboardTourVariant` for the same permission/flag-gated pattern.
 */
export const buildDashboardTourVariant = (
  enableAnnouncement: boolean
): TourVariant => ({
  tourId: 'employee-dashboard',
  initialMockState: {},
  buildSteps: (_setMockState, t) => {
    const steps: TourStep[] = [
      {
        target: '[data-tour="emp-dashboard-my-leave"]',
        title: t('employee_dashboard.my_leave.title'),
        content: t('employee_dashboard.my_leave.content'),
        placement: 'bottom' as const,
        skipBeacon: true,
      },
      {
        target: '[data-tour="emp-dashboard-on-leave"]',
        title: t('employee_dashboard.on_leave.title'),
        content: t('employee_dashboard.on_leave.content'),
        placement: 'top' as const,
        skipBeacon: true,
      },
    ];

    if (enableAnnouncement) {
      steps.push({
        target: '[data-tour="emp-dashboard-announcements"]',
        title: t('employee_dashboard.announcements.title'),
        content: t('employee_dashboard.announcements.content'),
        placement: 'left' as const,
        skipBeacon: true,
      });
    }

    steps.push(
      {
        target: '[data-tour="emp-dashboard-holidays"]',
        title: t('employee_dashboard.holidays.title'),
        content: t('employee_dashboard.holidays.content'),
        placement: 'left' as const,
        skipBeacon: true,
      },
      {
        target: '[data-tour="emp-dashboard-request-leave"]',
        title: t('employee_dashboard.request_leave.title'),
        content: t('employee_dashboard.request_leave.content'),
        placement: 'left' as const,
        skipBeacon: true,
      }
    );

    return steps;
  },
});

// ─── Leave Management ───────────────────────────────────────────────────────
export type LeaveRequestsTourMock = Record<string, unknown> & {
  showMockRequest: boolean;
  showFilters: boolean;
};

export const leaveRequestsTourVariant: TourVariant<LeaveRequestsTourMock> = {
  tourId: 'employee-leave-requests',
  initialMockState: { showMockRequest: false, showFilters: false },
  buildSteps: (setMockState, t) => [
    {
      target: '[data-tour="emp-leave-balances"]',
      title: t('employee_leave_management.balances.title'),
      content: t('employee_leave_management.balances.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="emp-leave-actions"]',
      title: t('employee_leave_management.actions.title'),
      content: t('employee_leave_management.actions.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="emp-leave-filters-panel"]',
      title: t('employee_leave_management.filters.title'),
      content: t('employee_leave_management.filters.content'),
      placement: 'left' as const,
      skipBeacon: true,
      spotlightPadding: 0,
      onEnter: () => setMockState((s) => ({ ...s, showFilters: true })),
      onExit: () => setMockState((s) => ({ ...s, showFilters: false })),
    },
    {
      target: '[data-tour="emp-leave-table"] tbody tr:first-child',
      title: t('employee_leave_management.table.title'),
      content: t('employee_leave_management.table.content'),
      placement: 'top' as const,
      skipBeacon: true,
      onEnter: () => setMockState((s) => ({ ...s, showMockRequest: true })),
      onExit: () => setMockState((s) => ({ ...s, showMockRequest: false })),
    },
  ],
};

// ─── Timesheet (time tracker) ────────────────────────────────────────────────

export type TimesheetTourMock = {
  mockClockState: ClockState | null;
  showMockEntry: boolean;
};

export const buildTimesheetTourVariant = (
  canCreate: boolean
): TourVariant<TimesheetTourMock> => ({
  tourId: 'employee-timesheet',
  initialMockState: { mockClockState: null, showMockEntry: false },
  buildSteps: (setMockState, t) => {
    const steps: TourStep[] = [
      {
        target: '[data-tour="timesheet-clock"]',
        title: t('employee_timesheet.clock.title'),
        content: t('employee_timesheet.clock.content'),
        placement: 'bottom',
        skipBeacon: true,
      },
      {
        target: '[data-tour="timesheet-clock-active"]',
        title: t('employee_timesheet.clocked_in.title'),
        content: t('employee_timesheet.clocked_in.content'),
        placement: 'bottom',
        skipBeacon: true,
        onEnter: () =>
          setMockState((s) => ({ ...s, mockClockState: ClockState.ClockedIn })),
      },
      {
        target: '[data-tour="timesheet-note"]',
        title: t('employee_timesheet.note.title'),
        content: t('employee_timesheet.note.content'),
        placement: 'left',
        skipBeacon: true,
        onEnter: () =>
          setMockState((s) => ({ ...s, mockClockState: ClockState.ClockedIn })),
      },
      {
        target: '[data-tour="timesheet-break"]',
        title: t('employee_timesheet.on_break.title'),
        content: t('employee_timesheet.on_break.content'),
        placement: 'top',
        skipBeacon: true,
        onEnter: () =>
          setMockState((s) => ({ ...s, mockClockState: ClockState.OnBreak })),
        onExit: () => setMockState((s) => ({ ...s, mockClockState: null })),
      },
      {
        target: '[data-tour="timesheet-date-range"]',
        title: t('employee_timesheet.date_range.title'),
        content: t('employee_timesheet.date_range.content'),
        placement: 'bottom',
        skipBeacon: true,
      },
    ];

    if (canCreate) {
      steps.push({
        target: '[data-tour="timesheet-add-entry"]',
        title: t('employee_timesheet.add_entry.title'),
        content: t('employee_timesheet.add_entry.content'),
        placement: 'left',
        skipBeacon: true,
      });
    }

    steps.push(
      {
        target: '[data-tour="timesheet-summary"]',
        title: t('employee_timesheet.summary.title'),
        content: t('employee_timesheet.summary.content'),
        placement: 'bottom',
        skipBeacon: true,
      },
      {
        target: '[data-tour="timesheet-table"]',
        title: t('employee_timesheet.table.title'),
        content: t('employee_timesheet.table.content'),
        placement: 'top',
        skipBeacon: true,
        onEnter: () => setMockState((s) => ({ ...s, showMockEntry: true })),
        onExit: () => setMockState((s) => ({ ...s, showMockEntry: false })),
      }
    );

    return steps;
  },
});

export const leaveCalendarTourVariant: TourVariant = {
  tourId: 'employee-leave-calendar',
  initialMockState: {},
  buildSteps: (_setMockState, t) => [
    {
      target: '[data-tour="emp-leave-calendar-header"]',
      title: t('employee_leave_management.calendar_header.title'),
      content: t('employee_leave_management.calendar_header.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="emp-leave-calendar-filters"]',
      title: t('employee_leave_management.calendar_filters.title'),
      content: t('employee_leave_management.calendar_filters.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="emp-leave-calendar"]',
      title: t('employee_leave_management.calendar.title'),
      content: t('employee_leave_management.calendar.content'),
      placement: 'top' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="calendar-legend"]',
      title: t('employee_leave_management.calendar_legend.title'),
      content: t('employee_leave_management.calendar_legend.content'),
      placement: 'top' as const,
      skipBeacon: true,
    },
  ],
};

// ─── Notes ──────────────────────────────────────────────────────────────────

export type NotesTourMock = Record<string, unknown> & {
  showMockNote: boolean;
};

export const notesTourVariant: TourVariant<NotesTourMock> = {
  tourId: 'employee-notes',
  initialMockState: { showMockNote: false },
  buildSteps: (setMockState, t) => [
    {
      target: '[data-tour="emp-notes-list"]',
      title: t('employee_notes.list.title'),
      content: t('employee_notes.list.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
      onEnter: () => setMockState((s) => ({ ...s, showMockNote: true })),
    },
    {
      target: '[data-tour="emp-notes-card"]:first-child',
      title: t('employee_notes.card.title'),
      content: t('employee_notes.card.content'),
      placement: 'top' as const,
      skipBeacon: true,
      onExit: () => setMockState((s) => ({ ...s, showMockNote: false })),
    },
  ],
};

// ─── Files ──────────────────────────────────────────────────────────────────

export type FilesTourMock = {
  showMock: boolean;
};

export const filesTourVariant: TourVariant<FilesTourMock> = {
  tourId: 'employee-files',
  initialMockState: { showMock: false },
  buildSteps: (setMockState, t) => [
    {
      target: '[data-tour="files-sidebar-header"]',
      title: t('employee_files.sidebar_header.title'),
      content: t('employee_files.sidebar_header.content'),
      placement: 'right' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="files-all-files"]',
      title: t('employee_files.all_files.title'),
      content: t('employee_files.all_files.content'),
      placement: 'right' as const,
      skipBeacon: true,
      onEnter: () => setMockState((s) => ({ ...s, showMock: false })),
    },
    {
      target: '[data-tour="files-folder-tree"]',
      title: t('employee_files.folder_tree.title'),
      content: t('employee_files.folder_tree.content'),
      placement: 'right' as const,
      skipBeacon: true,
      onEnter: () => setMockState((s) => ({ ...s, showMock: true })),
    },
    {
      target: '[data-tour="files-folder-header"]',
      title: t('employee_files.folder_header.title'),
      content: t('employee_files.folder_header.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="files-table"]',
      title: t('employee_files.table.title'),
      content: t('employee_files.table.content'),
      placement: 'top' as const,
      skipBeacon: true,
      onExit: () => setMockState((s) => ({ ...s, showMock: false })),
    },
  ],
};
